/**
 * RDTB — report water theft.
 *
 * ===========================================================================
 * THIS FORM USED TO TAKE NO NAME AT ALL
 * ===========================================================================
 * The note that stood here said a name field "eventually gets filled in, and
 * then a citizen who took a real risk is identifiable in a government
 * database". That reasoning was sound and the Department has overruled it for
 * a reason that answers it: bot protection stops a script, and does nothing
 * about a person filing twenty invented reports, each of which sends a
 * Sub-Divisional Officer out to a canal for nothing.
 *
 * So identity is now REQUIRED — name, father's name, CNIC, telephone number,
 * address — and so is the video link.
 *
 * What the citizen is owed in exchange is an honest account of where their
 * details go, which is the `Confidentiality` block below and the landing page
 * beside it. Both say the same thing the security rules enforce: the Public
 * Liaison Cell and the Secretary see who reported; the officers who go out to
 * the canal get a ticket number, a location, and the Cell's own description.
 * Not the name, and not the video either — footage of a canal bank can
 * identify whoever was standing there holding the telephone.
 *
 * Saying it here is not decoration. A form that collects a CNIC under a
 * heading promising anonymity is a lie to the person filling it in.
 *
 * DESIGN NOTES WORTH KEEPING
 *
 * There is no map. A map's tiles come from a third-party provider, and every
 * tile request carries the visitor's IP and their approximate location to that
 * provider — on the one page in this system that promises anonymity. GPS plus
 * manual coordinates plus a canal/RD hint covers the same ground, and saves
 * roughly 800 KB on a phone that may be on 2G.
 *
 * The video link is required because it is the only thing that lets an officer
 * judge whether a report is worth a site visit before making one.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  isWithinPunjab,
  MAX_ADDRESS_LENGTH,
  MAX_NAME_LENGTH,
  MAX_NOTES_LENGTH,
  parseVideoUrl,
  PUNJAB_BOUNDS,
  validateSubmission,
  type LatLng,
} from '@etawan/shared';
import { Turnstile, useTurnstile } from './Turnstile';

type Phase = 'form' | 'submitting' | 'done';

export function ReportForm() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('form');
  const [reporter, setReporter] = useState({
    name: '',
    fatherName: '',
    cnic: '',
    phone: '',
    address: '',
  });
  const setR = (k: keyof typeof reporter) => (v: string) => setReporter((r) => ({ ...r, [k]: v }));
  const [coords, setCoords] = useState<LatLng | null>(null);
  const [latText, setLatText] = useState('');
  const [lngText, setLngText] = useState('');
  const [locationHint, setLocationHint] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [gpsState, setGpsState] = useState<'idle' | 'locating' | 'denied' | 'unavailable'>('idle');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [trackingCode, setTrackingCode] = useState<string | null>(null);
  const { token, siteKey, isStubbed, reset: resetTurnstile, setToken } = useTurnstile();

  const video = videoUrl.trim() ? parseVideoUrl(videoUrl) : null;
  const videoInvalid = videoUrl.trim().length > 0 && video === null;

  const applyCoords = useCallback((p: LatLng) => {
    setCoords(p);
    setLatText(p.lat.toFixed(5));
    setLngText(p.lng.toFixed(5));
  }, []);

  const useMyLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGpsState('unavailable');
      return;
    }
    setGpsState('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        applyCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsState('idle');
      },
      () => setGpsState('denied'),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  }, [applyCoords]);

  const onManualCoords = useCallback((lat: string, lng: string) => {
    setLatText(lat);
    setLngText(lng);
    const p = { lat: Number(lat), lng: Number(lng) };
    setCoords(Number.isFinite(p.lat) && Number.isFinite(p.lng) ? p : null);
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorKey(null);

    if (!coords || !isWithinPunjab(coords)) {
      setErrorKey('report.error.coordinatesOutsidePunjab');
      return;
    }
    if (!video) {
      setErrorKey('report.error.videoNotRecognised');
      return;
    }

    // The SAME validator the edge function runs, so the citizen is told what
    // is wrong here rather than after a round trip. The edge copy is still the
    // one that decides.
    const check = validateSubmission({
      reporterName: reporter.name,
      reporterFatherName: reporter.fatherName,
      reporterCnic: reporter.cnic,
      reporterPhone: reporter.phone,
      reporterAddress: reporter.address,
      coordinates: coords,
      videoUrl: videoUrl.trim(),
      notes: notes.trim() || undefined,
      turnstileToken: token || 'pending',
    });
    if (!check.valid) {
      setErrorKey(check.reasonKey ?? 'report.error.serverError');
      return;
    }

    if (!token) {
      setErrorKey('report.error.turnstileMissing');
      return;
    }

    setPhase('submitting');
    try {
      const res = await fetch('/api/public-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reporterName: reporter.name.trim(),
          reporterFatherName: reporter.fatherName.trim(),
          reporterCnic: reporter.cnic.trim(),
          reporterPhone: reporter.phone.trim(),
          reporterAddress: reporter.address.trim(),
          coordinates: coords,
          locationHint: locationHint.trim() || undefined,
          videoUrl: videoUrl.trim(),
          notes: notes.trim() || undefined,
          turnstileToken: token,
        }),
      });
      const body = (await res.json()) as { trackingCode?: string; error?: string };

      if (!res.ok || !body.trackingCode) {
        setErrorKey(body.error ?? 'report.error.serverError');
        setPhase('form');
        resetTurnstile();
        return;
      }
      setTrackingCode(body.trackingCode);
      setPhase('done');
    } catch {
      setErrorKey('report.error.network');
      setPhase('form');
      resetTurnstile();
    }
  }

  if (phase === 'done' && trackingCode) {
    return <Submitted trackingCode={trackingCode} />;
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('report.heading')}</h1>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          {t('report.intro')}
        </p>
      </div>

      {/* ---- Who is reporting ------------------------------------------ */}
      {/* Placed FIRST, and with the confidentiality note attached to it
          rather than buried at the foot of the page. Somebody about to type
          their CNIC should read where it goes before they type it, not
          after. */}
      <div
        className="rounded-lg p-4 text-sm"
        style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
      >
        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
          {t('report.confidentialityTitle')}
        </p>
        <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
          {t('report.confidentialityBody')}
        </p>
      </div>

      <Fieldset legend={t('report.reporterLegend')} hint={t('report.reporterHelp')}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="reporterName"
            dir="inherit"
            label={t('report.reporterName')}
            value={reporter.name}
            onChange={setR('name')}
            maxLength={MAX_NAME_LENGTH}
          />
          <Field
            id="reporterFatherName"
            dir="inherit"
            label={t('report.reporterFatherName')}
            value={reporter.fatherName}
            onChange={setR('fatherName')}
            maxLength={MAX_NAME_LENGTH}
          />
          <Field
            id="reporterCnic"
            label={t('report.reporterCnic')}
            value={reporter.cnic}
            onChange={setR('cnic')}
            inputMode="numeric"
            placeholder="35202-1234567-1"
          />
          <Field
            id="reporterPhone"
            label={t('report.reporterPhone')}
            value={reporter.phone}
            onChange={setR('phone')}
            inputMode="tel"
            placeholder="03001234567"
          />
        </div>
        <div className="mt-3">
          <Field
            id="reporterAddress"
            dir="inherit"
            label={t('report.reporterAddress')}
            value={reporter.address}
            onChange={setR('address')}
            maxLength={MAX_ADDRESS_LENGTH}
          />
        </div>
        <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('report.phoneHelp')}
        </p>
      </Fieldset>

      {/* ---- Location -------------------------------------------------- */}
      <Fieldset legend={t('report.locationLegend')} hint={t('report.locationHelp')}>
        <button
          type="button"
          onClick={useMyLocation}
          disabled={gpsState === 'locating'}
          className="rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {gpsState === 'locating' ? t('report.locating') : t('report.useMyLocation')}
        </button>

        <div aria-live="polite">
          {gpsState === 'denied' && (
            <p className="mt-2 text-sm" style={{ color: 'var(--warning)' }}>
              {t('report.gpsDenied')}
            </p>
          )}
          {gpsState === 'unavailable' && (
            <p className="mt-2 text-sm" style={{ color: 'var(--warning)' }}>
              {t('report.gpsUnavailable')}
            </p>
          )}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field
            id="lat"
            label={t('report.latitude')}
            value={latText}
            onChange={(v) => onManualCoords(v, lngText)}
            inputMode="decimal"
            placeholder={`${PUNJAB_BOUNDS.minLat} – ${PUNJAB_BOUNDS.maxLat}`}
          />
          <Field
            id="lng"
            label={t('report.longitude')}
            value={lngText}
            onChange={(v) => onManualCoords(latText, v)}
            inputMode="decimal"
            placeholder={`${PUNJAB_BOUNDS.minLng} – ${PUNJAB_BOUNDS.maxLng}`}
          />
        </div>

        <div aria-live="polite">
          {coords && !isWithinPunjab(coords) && (
            <p className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>
              {t('report.error.coordinatesOutsidePunjab')}
            </p>
          )}
        </div>

        <div className="mt-4">
          <Field
            id="locationHint"
            label={t('report.locationHint')}
            value={locationHint}
            onChange={setLocationHint}
            placeholder={t('report.locationHintPlaceholder')}
            help={t('report.locationHintHelp')}
          />
        </div>
      </Fieldset>

      {/* ---- Video evidence -------------------------------------------- */}
      <Fieldset legend={t('report.videoLegend')} hint={t('report.videoHelp')}>
        <Field
          id="videoUrl"
          label={t('report.videoUrl')}
          value={videoUrl}
          onChange={setVideoUrl}
          type="url"
          placeholder="https://www.youtube.com/watch?v=…"
          invalid={videoInvalid}
        />
        <div aria-live="polite">
          {videoInvalid && (
            <p className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>
              {t('report.error.videoNotRecognised')}
            </p>
          )}
          {video && (
            <p className="mt-2 text-sm" style={{ color: 'var(--success)' }}>
              {t('report.videoRecognised', { platform: t(`platform.${video.platform}`) })}
            </p>
          )}
        </div>
      </Fieldset>

      {/* ---- Notes ------------------------------------------------------ */}
      <Fieldset legend={t('report.notesLegend')} hint={t('report.notesHelp')}>
        <label className="block text-sm font-medium" htmlFor="notes">
          {t('report.notes')}
        </label>
        <textarea
          id="notes"
          rows={4}
          maxLength={MAX_NOTES_LENGTH}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-1 w-full rounded-md px-3 py-2"
          style={{
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-subtle)',
          }}
        />
        <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          {notes.length} / {MAX_NOTES_LENGTH}
        </p>
      </Fieldset>

      <Turnstile siteKey={siteKey} isStubbed={isStubbed} onToken={setToken} />

      <div aria-live="assertive">
        {errorKey && (
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {t(errorKey)}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={phase === 'submitting'}
        className="rounded-md px-5 py-3 font-medium disabled:opacity-60"
        style={{ background: 'var(--accent)', color: '#fff' }}
      >
        {phase === 'submitting' ? t('report.submitting') : t('report.submit')}
      </button>

      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {t('report.privacyReminder')}
      </p>
    </form>
  );
}

/* -------------------------------------------------------------------------- */

function Submitted({ trackingCode }: { trackingCode: string }) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-lg p-6"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
    >
      <h1 className="text-2xl font-semibold">{t('report.done.heading')}</h1>
      <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
        {t('report.done.body')}
      </p>

      <div
        className="mt-5 rounded-md p-4 text-center"
        style={{ background: 'var(--bg-elevated)', border: '1px solid var(--accent)' }}
      >
        <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          {t('report.done.codeLabel')}
        </p>
        {/* dir="ltr" — the code is Latin/numeric and would otherwise be
            reordered by the bidi algorithm inside an Urdu page. */}
        <p className="mt-1 text-2xl font-semibold tracking-widest" dir="ltr">
          {trackingCode}
        </p>
      </div>

      {/* Said plainly, because it is the one thing that cannot be undone: the
          code is stored only as a hash, so nobody — including the Department —
          can look it up on the citizen's behalf. */}
      <p className="mt-4 text-sm" style={{ color: 'var(--warning)' }}>
        {t('report.done.writeItDown')}
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/track"
          className="rounded-md px-4 py-2 text-sm font-medium"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {t('report.done.checkStatus')}
        </Link>
        <Link
          to="/"
          className="rounded-md px-4 py-2 text-sm font-medium"
          style={{
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          {t('report.done.backHome')}
        </Link>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Fieldset({
  legend,
  hint,
  children,
}: {
  legend: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset
      className="rounded-lg p-5"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
    >
      <legend className="px-2 text-sm font-semibold">{legend}</legend>
      {hint && (
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          {hint}
        </p>
      )}
      {children}
    </fieldset>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  placeholder,
  help,
  invalid,
  maxLength,
  dir = 'ltr',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: 'decimal' | 'text' | 'numeric' | 'tel';
  placeholder?: string;
  help?: string;
  invalid?: boolean;
  maxLength?: number;
  /**
   * Latin by default, because coordinates, URLs, CNICs and telephone numbers
   * are Latin whatever the interface language. A name or an address is not:
   * `inherit` lets it follow the page, so somebody writing their name in Urdu
   * does not get it laid out left to right.
   */
  dir?: 'ltr' | 'inherit';
}) {
  return (
    <div>
      <label className="block text-sm font-medium" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        {...(dir === 'ltr' ? { dir: 'ltr' as const } : {})}
        className="mt-1 w-full rounded-md px-3 py-2"
        style={{
          background: 'var(--bg-elevated)',
          color: 'var(--text-primary)',
          border: `1px solid ${invalid ? 'var(--danger)' : 'var(--border-subtle)'}`,
        }}
      />
      {help && (
        <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          {help}
        </p>
      )}
    </div>
  );
}
